<!-- eslint-disable vue/multi-word-component-names -->
<script lang="ts">
export type EditorProps = {
  color: 'primary' | 'secondary';
  error?: boolean;
  plugins?: string[] | string;
  toolbar?: string[] | string;
  menubar?: string[] | string;
  modelValue?: string;
  hiddenPlugins?: string[];
};
</script>
<script setup lang="ts">
import Editor from '@tinymce/tinymce-vue';
import { computed, Ref, ref } from 'vue';
import { Editor as EditorClass } from 'tinymce';

// Import TinyMCE core first
import 'tinymce/tinymce.min.js';

// Then import the theme, model, and skins
import 'tinymce/themes/silver/theme.min.js';
import 'tinymce/models/dom/model.min.js';
import 'tinymce/skins/ui/oxide/skin.js';

// Import plugins
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/image';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/emoticons/js/emojis';
import 'tinymce/plugins/link';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/table';

// Import icons last (after TinyMCE core is loaded)
import 'tinymce/icons/default/icons.min.js';

// Import content styles
import 'tinymce/skins/ui/oxide/content.js';

const props = defineProps<EditorProps>();
const emit = defineEmits<{
  (event: 'editor:init', editor: EditorClass): void;
  (event: 'blur'): void;
  (event: 'update:modelValue', value: string): void;
}>();

const editor: Ref<EditorClass | null> = ref(null);
const DEFAULT_PLUGINS = 'lists table image emoticons link fullscreen wordcount';
const DEFAULT_TOOLBAR =
  'undo redo blocks bold italic underline strikethrough alignleft aligncenter alignright alignjustify link image emoticons fullscreen';
const allowedPlugins = new Set(['advlist', 'image', 'code', 'fullscreen', 'emoticons', 'link', 'wordcount', 'lists', 'table']);
const allowedToolbar = new Set([
  'undo',
  'redo',
  'blocks',
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'alignleft',
  'aligncenter',
  'alignright',
  'alignjustify',
  'link',
  'image',
  'emoticons',
  'fullscreen',
  'bullist',
  'numlist',
  'table',
]);

const normalizePluginList = (value?: string[] | string): string[] => {
  if (!value) return [];
  const list = Array.isArray(value) ? value : value.split(' ').filter(Boolean);
  const invalid = list.filter(plugin => !allowedPlugins.has(plugin));
  if (invalid.length) {
    console.error('[ElInputRichText] Invalid plugins:', invalid);
  }
  return list.filter(plugin => allowedPlugins.has(plugin));
};

const normalizeToolbarList = (value?: string[] | string): string[] => {
  if (!value) return [];
  const list = Array.isArray(value) ? value : value.split(' ').filter(Boolean);
  const invalid = list.filter(button => !allowedToolbar.has(button));
  if (invalid.length) {
    console.error('[ElInputRichText] Invalid toolbar buttons:', invalid);
  }
  return list.filter(button => allowedToolbar.has(button));
};

const hiddenPlugins = computed(() => new Set(normalizePluginList(props.hiddenPlugins)));

const plugins = computed(() => {
  const base = normalizePluginList(props.plugins ?? DEFAULT_PLUGINS);
  return base.filter(plugin => !hiddenPlugins.value.has(plugin));
});

const pluginToolbarMap: Record<string, string[]> = {
  link: ['link'],
  image: ['image'],
  emoticons: ['emoticons'],
  lists: ['bullist', 'numlist'],
  advlist: ['bullist', 'numlist'],
  table: ['table'],
  code: ['code'],
  fullscreen: ['fullscreen'],
  wordcount: [],
};

const toolbarButtonsToHide = computed(() => {
  return [...hiddenPlugins.value].flatMap(plugin => pluginToolbarMap[plugin] ?? []);
});

const toolbar = computed(() =>
  normalizeToolbarList(props.toolbar ?? DEFAULT_TOOLBAR)
    .filter(button => !toolbarButtonsToHide.value.includes(button))
    .join(' '),
);
const menubar = computed(() => props.menubar ?? '');

const className = computed(() => {
  if (props.error) return 'tox--error';
  return props.color === 'primary' ? 'tox--primary' : 'tox--secondary';
});

defineExpose<{
  editor: Ref<EditorClass | null>;
}>({
  editor,
});
</script>
<template>
  <div :class="className">
    <Editor
      onblur="emit('blur')"
      :model-value="props.modelValue"
      :init="{
        promotion: false,
        branding: false,
        statusbar: true,
        plugins: plugins,
        toolbar: toolbar,
        menubar: menubar,
        setup: (e: EditorClass) => {
          e.on('init', () => {
            editor = e;
          });
          emit('editor:init', e);
        },
      }"
      @update:model-value="(v: string) => emit('update:modelValue', v)"
    />
  </div>
</template>
